// SPDX-License-Identifier: GPL-3.0-or-later

import { getExtensionState } from '../ext/detect';
import {
  loadStoredRoot,
  pickRoot,
  queryRootPermission,
  requestRootPermission,
  supportsFileSystemAccess,
} from '../fs/root';
import { navigate } from '../router';
import { el } from './dom';

export const EXTENSION_STORE_URL =
  'https://chromewebstore.google.com/detail/applify/pdjejhfebimebgfealafmebbabjnhkfg';
export const INJINARY_STORE_URL =
  'https://chromewebstore.google.com/detail/injinary-wallet/emnpfdhpjmgbdgmbpcbloncillceljgp';

export interface OnboardingOptions {
  onReady(handle: FileSystemDirectoryHandle): void;
}

function step(num: string, title: string, desc: string, trailing?: HTMLElement): HTMLElement {
  return el(
    'div',
    { class: 'step' },
    el('div', { class: 'step-num' }, num),
    el(
      'div',
      { class: 'step-body' },
      el('div', { class: 'step-title' }, title),
      el('div', { class: 'step-desc' }, desc),
    ),
    trailing ?? el('span', {}, ''),
  );
}

export function renderOnboarding(opts: OnboardingOptions): HTMLElement {
  const container = el('section', { class: 'onboarding page-narrow' });
  const extensionInstalled = getExtensionState().installed;
  const status = el(
    'div',
    {
      class: 'inline-banner info',
      id: 'onboard-status',
      role: 'status',
    },
    'Pick any folder on your device — Applify keeps your jobs, CVs and cover letters inside it. Nothing leaves your machine until you opt in to AI features.',
  );

  const onPick = async (): Promise<void> => {
    try {
      const handle = await pickRoot();
      opts.onReady(handle);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      status.className = 'inline-banner error';
      status.textContent = `Folder picker failed: ${err instanceof Error ? err.message : String(err)}`;
    }
  };

  const onResume = async (): Promise<void> => {
    const handle = await loadStoredRoot();
    if (!handle) {
      status.className = 'inline-banner warn';
      status.textContent = 'No saved folder. Pick one to continue.';
      return;
    }
    const permission = await requestRootPermission(handle);
    if (permission === 'granted') {
      opts.onReady(handle);
    } else {
      status.className = 'inline-banner warn';
      status.textContent = 'Permission was not granted. Try again, or pick a different folder.';
    }
  };

  if (!supportsFileSystemAccess()) {
    container.append(
      renderHero(),
      el(
        'div',
        { class: 'unsupported' },
        el('div', { class: 'eyebrow' }, 'Browser not supported yet'),
        el(
          'p',
          { class: 'lede', style: 'margin-top: 0.5rem;' },
          'Applify needs the File System Access API. Use Chrome, Edge, Brave, Opera, or Arc. Firefox and Safari support (an IndexedDB-backed "lite mode") is planned.',
        ),
        el(
          'p',
          { style: 'margin-top: 0.75rem; font-size: 0.78rem;' },
          el('a', { href: '#/smoke' }, 'Check what your browser supports →'),
        ),
      ),
    );
    return container;
  }

  const buttons = el('div', { class: 'actions-row', style: 'margin-top: 0.5rem;' });

  void (async () => {
    const existing = await loadStoredRoot();
    if (existing) {
      const permission = await queryRootPermission(existing);
      if (permission === 'granted') {
        opts.onReady(existing);
        return;
      }
      buttons.append(
        el(
          'button',
          { class: 'btn primary large', onclick: onResume },
          el('span', {}, '↻'),
          `Resume — ${existing.name}`,
        ),
        el('button', { class: 'btn large', onclick: onPick }, 'Pick a different folder'),
      );
    } else {
      buttons.append(
        el(
          'button',
          { class: 'btn primary large', onclick: onPick },
          el('span', {}, '📂'),
          'Choose a folder',
        ),
      );
    }
  })();

  const steps = el(
    'div',
    { class: 'steps' },
    step(
      '1',
      'Pick a folder',
      'A regular folder on your device. Applify reads and writes jobs and documents inside it.',
    ),
    extensionInstalled
      ? step(
        '2',
        'Browser extension installed',
        'You\'re all set — a one-click "Save job" button now rides along while you browse LinkedIn, Workday, Glassdoor and more.',
        el('span', { class: 'step-done' }, '✓ Installed'),
      )
      : step(
        '2',
        'Install the browser extension',
        'Adds a one-click "Save job" button while you browse LinkedIn, Workday, Glassdoor and more.',
        el(
          'a',
          {
            class: 'btn compact',
            href: EXTENSION_STORE_URL,
            target: '_blank',
            rel: 'noreferrer',
          },
          'Install →',
        ),
      ),
    step(
      '3',
      'Tell Applify about you',
      'Paste your CV and the kinds of roles you want. Optional, but unlocks tailored CV + cover letter generation.',
    ),
    step(
      '4',
      'Install the Injinary Wallet',
      'Required for AI-powered CV and cover letter generation. Holds your Anthropic key and pays per request.',
      el(
        'a',
        {
          class: 'btn compact',
          href: INJINARY_STORE_URL,
          target: '_blank',
          rel: 'noreferrer',
        },
        'Install →',
      ),
    ),
  );

  const reassurance = el(
    'div',
    { class: 'reassurance' },
    el(
      'div',
      {},
      el('div', { class: 'label' }, 'Where it runs'),
      el('div', { class: 'value' }, 'Your browser'),
    ),
    el(
      'div',
      {},
      el('div', { class: 'label' }, 'Your data'),
      el('div', { class: 'value' }, 'Stays on disk'),
    ),
    el(
      'div',
      {},
      el('div', { class: 'label' }, 'AI calls'),
      el('div', { class: 'value' }, 'Opt-in only'),
    ),
  );

  container.append(
    renderHero(),
    status,
    buttons,
    el(
      'div',
      { class: 'stack stack-md', style: 'margin-top: 1rem;' },
      el('div', { class: 'eyebrow' }, 'How it works'),
      steps,
    ),
    el(
      'div',
      { class: 'stack stack-md' },
      el('div', { class: 'eyebrow' }, 'Reassurances'),
      reassurance,
    ),
    el(
      'p',
      { class: 'hint', style: 'text-align: center; margin-top: 0.5rem;' },
      el(
        'a',
        {
          href: '#/privacy',
          onclick: (e: Event) => {
            e.preventDefault();
            navigate('privacy');
          },
        },
        'Read the privacy policy →',
      ),
    ),
  );

  return container;
}

function renderHero(): HTMLElement {
  return el(
    'div',
    { class: 'onboarding-hero' },
    el('div', { class: 'eyebrow' }, 'Welcome'),
    el(
      'h1',
      { class: 'display-1' },
      'Job hunting,',
      el('br', {}),
      el('em', {}, 'without the headache.'),
    ),
    el(
      'p',
      { class: 'lede' },
      'Capture jobs from LinkedIn and dozens of other boards with one click. Track every application, generate tailored CVs and cover letters with your own AI key — everything stays on your own computer, with no account and nothing uploaded.',
    ),
  );
}
